import { useCallback, useMemo } from 'react';

import { useCustomSettings } from './use-custom-settings';

import type { CustomSettingConfig, UseCustomSettingReturn } from '../types';

/**
 * Hook to manage a specific custom setting with type safety
 * @param config - Configuration object defining the setting name, default value, and schema
 * @returns Object with current value, loading state, and update function
 */
export function useCustomSetting<T>(config: CustomSettingConfig<T>): UseCustomSettingReturn<T> {
  const { settings, updateSetting, createSetting } = useCustomSettings();

  const { name, defaultValue, schema } = config;

  // Get the current setting
  const currentSetting = useMemo(() => {
    return settings.get(name);
  }, [settings, name]);

  // Get the current value with fallback to default
  const value = useMemo(() => {
    if (!currentSetting) return defaultValue;

    // Validate with schema if provided
    if (schema) {
      try {
        return schema.parse(currentSetting.value);
      } catch (error) {
        console.warn(`Schema validation failed for setting '${name}':`, error);
        return defaultValue;
      }
    }

    return currentSetting.value as T;
  }, [currentSetting, defaultValue, schema, name]);

  // Check if setting exists
  const exists = useMemo(() => !!currentSetting, [currentSetting]);

  // Get setting ID
  const settingId = useMemo(() => currentSetting?.id ?? null, [currentSetting]);

  // Update value function
  const updateValue = useCallback(
    async (newValue: T): Promise<void> => {
      // Validate with schema if provided
      if (schema) {
        try {
          schema.parse(newValue);
        } catch (error) {
          throw new Error(`Invalid value for setting '${name}': ${error}`);
        }
      }

      if (exists) {
        await updateSetting(name, newValue);
      } else {
        await createSetting(name, newValue);
      }
    },
    [schema, name, exists, updateSetting, createSetting]
  );

  // Check if any mutation is in progress
  const isUpdating = useMemo(() => {
    // This would need to be enhanced to track mutation states
    // For now, returning false as the mutations are handled in useCustomSettings
    return false;
  }, []);

  return {
    value,
    isUpdating,
    updateValue,
    exists,
    settingId,
  };
}

/**
 * Simplified hook for custom settings with just name and default value
 * @param name - Setting name
 * @param defaultValue - Default value if setting doesn't exist
 * @returns Object with current value and update function
 */
export function useSimpleCustomSetting<T>(name: string, defaultValue: T) {
  return useCustomSetting<T>({ name, defaultValue });
}
